import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { withTimeout } from '../../shared/withTimeout';

import {
	SAPViewerStartedPetitionsCount,
	type SapViewerStartedPetitionsCountQuery,
	type SapViewerStartedPetitionsCountQueryVariables,
} from './getViewerStartedPetitionsCount.graphql';

// The caller awaits this before resolving the post-publish redirect, so a hung request must reject
// rather than block publish indefinitely — same rationale and value as claimSocialStarterPetitionAngles.
const STARTED_PETITIONS_COUNT_TIMEOUT_MS = 8000;

/**
 * Checks whether the petition just published is the viewer's first, by re-reading their started
 * petitions right after publish. The query includes inactive/deleted petitions (the connection
 * excludes both by default) so a viewer whose only prior petition was later deleted or deactivated
 * still correctly reads as not-first — mirrors the backend's own "ever published" check for the
 * equivalent first-petition gating on starter_experience_copilot_start_strong.
 *
 * Counts nodes rather than trusting the connection's raw `totalCount`: `startedPetitionsConnection`
 * also returns `UNPUBLISHED` petitions (abandoned drafts that got far enough to create a backend
 * record but were never actually published — the same connection is used by Manage Assets for
 * exactly this status), so `totalCount` alone would overcount and wrongly exclude a viewer whose
 * only prior activity was an abandoned draft.
 *
 * Only ever confirms "first" from a *complete* view of the viewer's history (`!hasNextPage`): with
 * `first: 10`, a viewer with more than 10 started petitions could have an older published one sitting
 * beyond this page that this function never sees. Concluding "first" from an incomplete page in that
 * case would risk the dangerous direction — over-attributing an exposure to the wrong petition — so
 * an incomplete page (or more than one non-UNPUBLISHED node found) fails closed to `false` instead.
 *
 * Best-effort and fails closed (`false`) on any error, timeout, unexpected shape, or inconclusive
 * page — this only ever gates an experiment exposure, not the redirect itself, and a missed exposure
 * is preferable to over-attributing one to the wrong petition.
 */
export async function isViewerFirstPublishedPetition(utilityContext: UtilityContext): Promise<boolean> {
	try {
		const { data } = await withTimeout(
			utilityContext.gql.fetch<SapViewerStartedPetitionsCountQuery, SapViewerStartedPetitionsCountQueryVariables>({
				query: SAPViewerStartedPetitionsCount,
				rejectOnError: true,
			}),
			STARTED_PETITIONS_COUNT_TIMEOUT_MS,
			`Check viewer started petitions count timed out after ${STARTED_PETITIONS_COUNT_TIMEOUT_MS}ms`,
		);

		const connection = data.viewer.startedPetitionsConnection;
		if (connection.__typename !== 'StartedPetitionsConnectionSuccess') return false;

		const publishedCount = connection.nodes.filter((node) => node.status !== 'UNPUBLISHED').length;
		return publishedCount === 1 && !connection.pageInfo.hasNextPage;
	} catch (error) {
		void utilityContext.errorReporter.report({
			error:
				error instanceof Error ? error : new Error('Failed to check viewer started petitions count', { cause: error }),
		});
		return false;
	}
}
